import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconSearch } from '@sorare/core/src/atoms/icons/system/iconSearch.ts';
import { TextInput } from '@sorare/core/src/atoms/inputs/TextInput';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useDebouncedCallback } from '@sorare/core/src/hooks/useDebouncedCallback';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';

import { JoinSquadDialog } from '#src/components/squad/JoinSquadDialog/index.tsx';

import { SquadSearchResult } from './SquadSearchResult/index.tsx';
import type {
  SquadSearchQuery,
  SquadSearchQueryVariables,
} from './__generated__/index.graphql.ts';

const PAGE_SIZE = 20;

const SQUAD_SEARCH_QUERY = gql`
  query SquadSearchQuery(
    $sport: Sport!
    $query: String
    $first: Int!
    $after: String
  ) {
    cardSet {
      id
      searchSquads(sport: $sport, query: $query, first: $first, after: $after) {
        pageInfo {
          endCursor
          hasNextPage
        }
        nodes {
          id
          ...SquadSearchResult_squad
          ...JoinSquadDialog_squad
        }
      }
    }
  }
  ${SquadSearchResult.fragments.squad}
  ${JoinSquadDialog.fragments.squad}
` as TypedDocumentNode<SquadSearchQuery, SquadSearchQueryVariables>;

export const SquadSearch = () => {
  const { sport } = useSportContext();
  const { formatMessage } = useIntl();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const debounceQuery = useDebouncedCallback(setQuery, 300);

  const { data, loading, loadMore } = usePaginatedQuery(SQUAD_SEARCH_QUERY, {
    variables: { sport, query: query.trim() || null, first: PAGE_SIZE },
    connection: 'SquadConnection',
  });

  const searchSquads = data?.cardSet?.searchSquads;
  const squads = searchSquads?.nodes || [];

  const [selectedSquad, setSelectedSquad] = useState<
    (typeof squads)[number] | null
  >(null);
  const [joinDialogOpen, setJoinDialogOpen] = useState(false);

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, { after: searchSquads?.pageInfo.endCursor });
    },
    !!searchSquads?.pageInfo.hasNextPage,
    loading
  );

  return (
    <Vertical gap={2}>
      <TextInput
        className="rounded-full"
        type="search"
        autoComplete="off"
        autoCorrect="off"
        startAdornment={<FontAwesomeIcon icon={iconSearch} />}
        value={input}
        onChange={e => {
          setInput(e.target.value);
          debounceQuery(e.target.value);
        }}
        placeholder={formatMessage({ defaultMessage: 'Search a squad' })}
      />
      {squads.length > 0 ? (
        <Vertical gap={1}>
          {squads.map(squad => (
            <SquadSearchResult
              key={squad.id}
              squad={squad}
              onJoin={() => {
                setSelectedSquad(squad);
                setJoinDialogOpen(true);
              }}
            />
          ))}
          <InfiniteScrollLoader />
        </Vertical>
      ) : loading ? (
        <LoadingIndicator small />
      ) : (
        <BodyM className="text-center" color="var(--c-nd-600)">
          <FormattedMessage defaultMessage="No result found" />
        </BodyM>
      )}
      <JoinSquadDialog
        squad={selectedSquad}
        open={joinDialogOpen}
        onClose={() => setJoinDialogOpen(false)}
      />
    </Vertical>
  );
};
